import { useEffect, useRef, useState } from 'react';
import {
  getInstitutionalActs,
  getInstitutionalRules,
  getInstitutionalRuleVersions,
  getInstitutionalActDocument,
  InstitutionalRegisterApiError,
  type ReviewOrganization,
  type InstitutionalActRecord,
  type InstitutionalRuleRecord,
} from '@urbanik/api-client';
import {
  Button,
  CanonicalCard,
  SelectField,
  DataTable,
  StatePanel,
  canonicalStates,
  type DataColumn,
} from '@urbanik/ui';
import { InstitutionalRuleLifecycle } from './InstitutionalRuleLifecycle';
import { InstitutionalRuleEditor } from './InstitutionalRuleEditor';
import { InstitutionalActDocuments } from './InstitutionalActDocuments';
import { InstitutionalActLifecycle } from './InstitutionalActLifecycle';
import { InstitutionalActEditor } from './InstitutionalActEditor';
import { reviewApiBase } from './api-base';
function Table<T extends { id: string }>({
  rows,
  columns,
}: {
  rows: readonly T[];
  columns: readonly DataColumn<T>[];
}) {
  return (
    <DataTable
      rows={rows}
      columns={columns}
      visibleColumnIds={new Set(columns.map((c) => c.id))}
      sort={{ columnId: 'id', direction: 'ascending' }}
      onSort={() => {}}
      empty={<StatePanel state={canonicalStates.find((s) => s.kind === 'empty')!} />}
    />
  );
}
export function InstitutionalRegisterPanel({ organization }: { organization: ReviewOrganization }) {
  const [kind, setKind] = useState<'acts' | 'rules'>('acts');
  return (
    <section aria-label={'Registro de ' + organization.name}>
      <h2>{organization.name}</h2>
      <p>
        Entidad: {organization.code} · {organization.id}
      </p>
      <p>
        Ensayo institucional: actas con alta, edición y papelera según permisos. Carga e historial
        de PDF disponibles; alta de reglas fijas disponible. Versionado y anulación de reglas
        pendientes.
      </p>
      <div role="group" aria-label="Registro institucional">
        <Button aria-pressed={kind === 'acts'} onClick={() => setKind('acts')}>
          Actas
        </Button>
        <Button aria-pressed={kind === 'rules'} onClick={() => setKind('rules')}>
          Reglas
        </Button>
      </div>
      <RegisterContent key={organization.id + kind} organization={organization} kind={kind} />
    </section>
  );
}
function RegisterContent({
  organization,
  kind,
}: {
  organization: ReviewOrganization;
  kind: 'acts' | 'rules';
}) {
  const [newRule, setNewRule] = useState(false);
  const [ruleVersion, setRuleVersion] = useState<InstitutionalRuleRecord | null>(null);
  const [ruleLifecycle, setRuleLifecycle] = useState<InstitutionalRuleRecord | null>(null);
  const [documents, setDocuments] = useState<InstitutionalActRecord | null>(null);
  const [lifecycle, setLifecycle] = useState<InstitutionalActRecord | null>(null);
  const [actState, setActState] = useState<'active' | 'trash' | 'all'>('active');
  const [notice, setNotice] = useState('');
  const [editor, setEditor] = useState<InstitutionalActRecord | null | undefined>(undefined);
  const [acts, setActs] = useState<readonly InstitutionalActRecord[]>([]);
  const [rules, setRules] = useState<readonly InstitutionalRuleRecord[]>([]);
  const [history, setHistory] = useState<readonly InstitutionalRuleRecord[] | null>(null);
  const [loading, setLoading] = useState(true),
    [error, setError] = useState<unknown>(null),
    [reload, setReload] = useState(0),
    [busy, setBusy] = useState(false);
  const action = useRef<AbortController | null>(null);
  const allowed = organization.permissions.includes('governance.' + kind + '.read');
  useEffect(() => {
    const controller = new AbortController();
    setActs([]);
    setRules([]);
    setHistory(null);
    setError(null);
    setLoading(true);
    if (!allowed) {
      setLoading(false);
      return () => controller.abort();
    }
    void (async () => {
      try {
        if (kind === 'acts') {
          const data = await getInstitutionalActs(
            reviewApiBase,
            organization.id,
            controller.signal,
          );
          if (!controller.signal.aborted) setActs(data.items);
        } else {
          const data = await getInstitutionalRules(
            reviewApiBase,
            organization.id,
            controller.signal,
          );
          if (!controller.signal.aborted) setRules(data.items);
        }
      } catch (e) {
        if (!controller.signal.aborted) setError(e);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
    return () => {
      controller.abort();
      action.current?.abort();
    };
  }, [organization.id, kind, allowed, reload]);
  async function versions(rule: InstitutionalRuleRecord) {
    action.current?.abort();
    const controller = new AbortController();
    action.current = controller;
    setHistory(null);
    setError(null);
    setBusy(true);
    try {
      const data = await getInstitutionalRuleVersions(
        reviewApiBase,
        organization.id,
        rule.id,
        rule.lineageId,
        controller.signal,
      );
      if (!controller.signal.aborted) setHistory(data.items);
    } catch (e) {
      if (!controller.signal.aborted) setError(e);
    } finally {
      if (!controller.signal.aborted) setBusy(false);
    }
  }
  async function download(act: InstitutionalActRecord) {
    action.current?.abort();
    const controller = new AbortController();
    action.current = controller;
    setError(null);
    setBusy(true);
    try {
      const blob = await getInstitutionalActDocument(
        reviewApiBase,
        organization.id,
        act.id,
        controller.signal,
      );
      if (controller.signal.aborted) return;
      const url = URL.createObjectURL(blob),
        link = document.createElement('a');
      link.href = url;
      link.download = 'acta-' + act.id + '.pdf';
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      if (!controller.signal.aborted) setError(e);
    } finally {
      if (!controller.signal.aborted) setBusy(false);
    }
  }
  const actColumns: DataColumn<InstitutionalActRecord>[] = [
    { id: 'number', header: 'Acta', accessor: (r) => r.number },
    { id: 'subject', header: 'Asunto', accessor: (r) => r.subject },
    { id: 'date', header: 'Fecha', accessor: (r) => r.date },
    {
      id: 'status',
      header: 'Estado',
      accessor: (r) =>
        r.deletedAt
          ? 'En papelera'
          : r.documentStatus === 'approved'
            ? 'Documento aprobado'
            : 'Documento pendiente',
    },
    {
      id: 'document',
      header: 'Documento',
      accessor: () => '',
      cell: (r) =>
        r.hasDocument ? (
          <Button disabled={busy} onClick={() => void download(r)}>
            Descargar PDF
          </Button>
        ) : (
          'Sin documento'
        ),
    },
  ];
  if (organization.permissions.includes('governance.acts.update'))
    actColumns.push({
      id: 'edit',
      header: 'Edición',
      accessor: () => '',
      cell: (r) => (
        <Button disabled={busy || !!r.deletedAt} onClick={() => setEditor(r)}>
          Editar acta
        </Button>
      ),
    });
  if (organization.permissions.includes('governance.acts.delete'))
    actColumns.push({
      id: 'lifecycle',
      header: 'Papelera',
      accessor: () => '',
      cell: (r) => (
        <Button disabled={busy} onClick={() => setLifecycle(r)}>
          {r.deletedAt ? 'Restaurar acta' : 'Enviar a papelera'}
        </Button>
      ),
    });
  actColumns.push({
    id: 'documents',
    header: 'Versiones PDF',
    accessor: () => '',
    cell: (r) => (
      <Button disabled={busy} onClick={() => setDocuments(r)}>
        Documentos del acta
      </Button>
    ),
  });
  const ruleColumns: DataColumn<InstitutionalRuleRecord>[] = [
    { id: 'code', header: 'Regla', accessor: (r) => r.code },
    { id: 'concept', header: 'Concepto', accessor: (r) => r.conceptName },
    { id: 'act', header: 'Acta', accessor: (r) => r.actNumber },
    { id: 'amount', header: 'Importe USD', accessor: (r) => r.amount },
    {
      id: 'version',
      header: 'Versión',
      accessor: (r) => String(r.versionNumber) + (r.isCurrent ? ' · Última' : ' · Histórica'),
    },
    { id: 'state', header: 'Estado', accessor: (r) => (r.deletedAt ? 'Anulada' : 'Sin anular') },
    {
      id: 'versions',
      header: 'Historia',
      accessor: () => '',
      cell: (r) =>
        organization.permissions.includes('governance.rule_versions.read') ? (
          <Button disabled={busy} onClick={() => void versions(r)}>
            Ver versiones
          </Button>
        ) : (
          'Sin permiso de historia'
        ),
    },
  ];
  if (organization.permissions.includes('governance.rule_versions.create'))
    ruleColumns.push({
      id: 'new-version',
      header: 'Nueva versión',
      accessor: () => '',
      cell: (r) => (
        <Button disabled={busy || !!r.deletedAt} onClick={() => setRuleVersion(r)}>
          Nueva versión
        </Button>
      ),
    });
  if (organization.permissions.includes('governance.rules.lifecycle'))
    ruleColumns.push({
      id: 'rule-lifecycle',
      header: 'Ciclo de vida',
      accessor: () => '',
      cell: (r) => (
        <Button disabled={busy} onClick={() => setRuleLifecycle(r)}>
          {r.deletedAt ? 'Restaurar' : 'Anular'}
        </Button>
      ),
    });
  if (!allowed) return <StatePanel state={canonicalStates.find((s) => s.kind === 'forbidden')!} />;
  if (loading) return <StatePanel state={canonicalStates[0]!} />;
  if (error)
    return (
      <StatePanel
        state={canonicalStates.find(
          (s) =>
            s.kind ===
            (error instanceof InstitutionalRegisterApiError && error.status === 403
              ? 'forbidden'
              : 'error'),
        )!}
        onAction={() => setReload((v) => v + 1)}
      />
    );
  return (
    <CanonicalCard>
      {notice ? <p role="status">{notice}</p> : null}
      {kind === 'rules' && organization.permissions.includes('governance.rules.create') ? (
        <Button onClick={() => setNewRule(true)}>Nueva regla</Button>
      ) : null}
      {ruleVersion ? (
        <InstitutionalRuleEditor
          organization={organization}
          rule={ruleVersion}
          onClose={() => setRuleVersion(null)}
          onSaved={() => {
            setRuleVersion(null);
            setNotice('Nueva versión inmutable creada.');
            setReload((v) => v + 1);
          }}
        />
      ) : null}
      {ruleLifecycle ? (
        <InstitutionalRuleLifecycle
          organization={organization}
          rule={ruleLifecycle}
          onClose={() => setRuleLifecycle(null)}
          onSaved={() => {
            const wasDeleted = !!ruleLifecycle.deletedAt;
            setRuleLifecycle(null);
            setNotice(
              wasDeleted ? 'Regla restaurada.' : 'Regla anulada; su historial se conserva.',
            );
            setReload((v) => v + 1);
          }}
        />
      ) : null}
      {newRule ? (
        <InstitutionalRuleEditor
          organization={organization}
          onClose={() => setNewRule(false)}
          onSaved={() => {
            setNewRule(false);
            setNotice('Regla creada en esta entidad. No se generaron obligaciones ni pagos.');
            setReload((v) => v + 1);
          }}
        />
      ) : null}
      {documents ? (
        <InstitutionalActDocuments
          organization={organization}
          act={documents}
          onClose={() => {
            setDocuments(null);
            setReload((v) => v + 1);
          }}
        />
      ) : null}
      {kind === 'acts' ? (
        <SelectField
          label="Estado de actas"
          value={actState}
          options={[
            { value: 'active', label: 'Fuera de papelera' },
            { value: 'trash', label: 'En papelera' },
            { value: 'all', label: 'Todas' },
          ]}
          onValueChange={(value) => setActState(value as typeof actState)}
        />
      ) : null}
      {lifecycle ? (
        <InstitutionalActLifecycle
          organization={organization}
          act={lifecycle}
          onClose={() => {
            setLifecycle(null);
            setReload((v) => v + 1);
          }}
          onSaved={() => {
            setNotice(
              lifecycle.deletedAt
                ? 'Acta restaurada.'
                : 'Acta enviada a papelera. Puede restaurarse.',
            );
            setLifecycle(null);
            setReload((v) => v + 1);
          }}
        />
      ) : null}
      {kind === 'acts' && organization.permissions.includes('governance.acts.create') ? (
        <Button onClick={() => setEditor(null)}>Nueva acta</Button>
      ) : null}
      {editor !== undefined ? (
        <InstitutionalActEditor
          organization={organization}
          act={editor}
          onClose={() => {
            setEditor(undefined);
            setReload((v) => v + 1);
          }}
          onSaved={() => {
            setEditor(undefined);
            setReload((v) => v + 1);
          }}
        />
      ) : null}
      {kind === 'acts' ? (
        <Table
          rows={acts.filter(
            (a) => actState === 'all' || (actState === 'trash' ? !!a.deletedAt : !a.deletedAt),
          )}
          columns={actColumns}
        />
      ) : (
        <Table rows={rules} columns={ruleColumns} />
      )}
      {busy ? <p role="status">Consultando…</p> : null}
      {history ? (
        <section aria-label="Versiones de la regla">
          <h3>Versiones · {organization.name}</h3>
          <Table rows={history} columns={ruleColumns.filter((c) => c.id !== 'versions')} />
        </section>
      ) : null}
    </CanonicalCard>
  );
}
